import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/ui/Layout';
import { IconDownload, IconFileTypePdf, IconClipboardList } from '@tabler/icons-react';
import { generateMonthlyCompliancePDF } from '../../utils/pdf';
import Papa from 'papaparse';

export default function CheckHistory() {
  const navigate = useNavigate();
  const { orgId, userProfile, org } = useAuth();
  const [checks, setChecks] = useState([]);
  const [assets, setAssets] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [branch, setBranch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exportingPdf, setExportingPdf] = useState(false);

  const branchId = userProfile?.branchId;
  const resolvedOrgId = orgId || userProfile?.orgId;

  useEffect(() => {
    const load = async () => {
      if (!resolvedOrgId) {
        setLoading(false);
        return;
      }
      try {
        const allChecks = [];

        if (branchId) {
          const [checksSnap, branchSnap, assetsSnap, incidentsSnap] = await Promise.all([
            getDocs(query(
              collection(db, 'organisations', resolvedOrgId, 'branches', branchId, 'checkRecords'),
              orderBy('submittedAt', 'desc')
            )),
            getDocs(collection(db, 'organisations', resolvedOrgId, 'branches')),
            getDocs(collection(db, 'organisations', resolvedOrgId, 'branches', branchId, 'assets')),
            getDocs(collection(db, 'organisations', resolvedOrgId, 'incidents')),
          ]);

          checksSnap.docs.forEach(d => allChecks.push({ id: d.id, branchId, ...d.data() }));

          const branchDoc = branchSnap.docs.find(d => d.id === branchId);
          if (branchDoc) setBranch({ id: branchDoc.id, ...branchDoc.data() });

          setAssets(assetsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
          setIncidents(incidentsSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(i => i.branchId === branchId));
        } else {
          const branchesSnap = await getDocs(collection(db, 'organisations', resolvedOrgId, 'branches'));
          for (const branchDoc of branchesSnap.docs) {
            const snap = await getDocs(query(
              collection(db, 'organisations', resolvedOrgId, 'branches', branchDoc.id, 'checkRecords'),
              orderBy('submittedAt', 'desc')
            ));
            snap.docs.forEach(d => allChecks.push({ id: d.id, branchId: branchDoc.id, branchName: branchDoc.data().name, ...d.data() }));
          }
          allChecks.sort((a, b) => {
            const aTime = a.submittedAt?.toDate ? a.submittedAt.toDate() : new Date(a.submittedAt || 0);
            const bTime = b.submittedAt?.toDate ? b.submittedAt.toDate() : new Date(b.submittedAt || 0);
            return bTime - aTime;
          });
        }

        setChecks(allChecks);
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    };
    load();
  }, [resolvedOrgId, branchId]);

  const passed = checks.filter(c => c.result === 'pass').length;
  const failed = checks.filter(c => c.result === 'fail').length;
  const issues = checks.filter(c => c.result === 'issues').length;

  const formatTime = (ts) => {
    if (!ts) return '—';
    const date = ts.toDate ? ts.toDate() : new Date(ts);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === today.toDateString()) {
      return `Today ${date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
    }
    if (date.toDateString() === yesterday.toDateString()) {
      return `Yesterday ${date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
    }
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const getInitials = name => name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '?';

  const avatarColours = [
    { bg: '#E6F1FB', color: '#185FA5' },
    { bg: '#EAF3DE', color: '#3B6D11' },
    { bg: '#EEEDFE', color: '#534AB7' },
    { bg: '#FCEBEB', color: '#A32D2D' },
    { bg: '#FDF0E8', color: '#993C1D' },
    { bg: '#E1F5EE', color: '#085041' },
  ];

  const resultStyle = r => r === 'pass'
    ? { bg: 'var(--color-success-light)', color: 'var(--color-success)', label: 'Pass' }
    : r === 'fail'
      ? { bg: 'var(--color-danger-light)', color: 'var(--color-danger)', label: 'Fail' }
      : { bg: 'var(--color-warning-light)', color: 'var(--color-warning)', label: 'Issues' };

  const handleExportCSV = () => {
    const data = checks.map(c => ({
      'Staff member': c.submittedByName || '—',
      'Asset': c.assetName || '—',
      'Registration': c.assetReg || '—',
      'Branch': c.branchName || c.branchId || '—',
      'Template': c.templateName || '—',
      'Date & time': formatTime(c.submittedAt),
      'Mileage': c.mileage ? `${c.mileage} mi` : '—',
      'Result': c.result || '—',
      'Failed items': c.failedItems?.join(', ') || '—',
    }));
    const csv = Papa.unparse(data);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `check-history-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportPDF = async () => {
    setExportingPdf(true);
    try {
      const monthLabel = new Date().toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
      generateMonthlyCompliancePDF(checks, assets, incidents, branch, org, monthLabel);
    } catch (err) {
      console.error(err);
    }
    setExportingPdf(false);
  };

  const branchName = branchId
    ? branchId.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
    : 'All branches';

  return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 400, marginBottom: 4 }}>Check history</h1>
            <p style={{ fontSize: 13, color: 'var(--color-text-tertiary)' }}>
              {branchName} · {loading ? '...' : `${checks.length} record${checks.length !== 1 ? 's' : ''}`}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={handleExportCSV}
              disabled={checks.length === 0}
              style={{ background: 'transparent', color: 'var(--color-text-secondary)', border: '0.5px solid var(--color-border-medium)', borderRadius: 'var(--radius-md)', padding: '7px 14px', fontSize: 12, cursor: checks.length === 0 ? 'not-allowed' : 'pointer', opacity: checks.length === 0 ? 0.5 : 1, display: 'flex', alignItems: 'center', gap: 5 }}
            >
              <IconDownload size={14} stroke={1.5} /> Export CSV
            </button>
            <button
              onClick={handleExportPDF}
              disabled={checks.length === 0 || exportingPdf}
              style={{ background: 'var(--color-brand)', color: '#fff', border: 'none', borderRadius: 'var(--radius-md)', padding: '7px 14px', fontSize: 12, cursor: checks.length === 0 || exportingPdf ? 'not-allowed' : 'pointer', opacity: checks.length === 0 || exportingPdf ? 0.5 : 1, display: 'flex', alignItems: 'center', gap: 5 }}
            >
              <IconFileTypePdf size={14} stroke={1.5} /> {exportingPdf ? 'Generating...' : 'Compliance PDF'}
            </button>
          </div>
        </div>

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, marginBottom: 16 }}>
          {[
            { label: 'Total checks', value: checks.length, colour: 'var(--color-text-primary)' },
            { label: 'Passed', value: passed, colour: 'var(--color-success)' },
            { label: 'Failed', value: failed, colour: 'var(--color-danger)' },
            { label: 'Issues', value: issues, colour: 'var(--color-warning)' },
          ].map(s => (
            <div key={s.label} style={{ background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', padding: '14px 16px' }}>
              <div style={{ fontSize: 28, fontWeight: 400, color: s.colour, marginBottom: 4 }}>{loading ? '—' : s.value}</div>
              <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{s.label}</div>
            </div>
          ))}
        </div>

        <div style={{ background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1.8fr 1.4fr 1.2fr 0.8fr 0.8fr 0.6fr 60px', gap: 0, padding: '8px 16px', borderBottom: '0.5px solid var(--color-border-light)', background: 'var(--color-bg-secondary)' }}>
            {['Staff member', 'Asset', 'Branch', 'Date & time', 'Mileage', 'Result', ''].map(h => (
              <div key={h} style={{ fontSize: 10, fontWeight: 400, color: 'var(--color-text-tertiary)', letterSpacing: '0.04em' }}>{h}</div>
            ))}
          </div>

          {loading ? (
            <div style={{ padding: 20, textAlign: 'center', fontSize: 13, color: 'var(--color-text-tertiary)' }}>Loading checks...</div>
          ) : checks.length === 0 ? (
            <div style={{ padding: '48px 32px', textAlign: 'center' }}>
              <IconClipboardList size={32} color="var(--color-text-tertiary)" stroke={1.5} style={{ marginBottom: 12 }} />
              <div style={{ fontSize: 14, fontWeight: 400, color: 'var(--color-text-primary)', marginBottom: 6 }}>No checks yet</div>
              <div style={{ fontSize: 13, color: 'var(--color-text-tertiary)' }}>Checks will appear here once operators start submitting walkarounds</div>
            </div>
          ) : checks.map((c, i) => {
            const rs = resultStyle(c.result);
            const colours = avatarColours[i % avatarColours.length];
            return (
              <div
                key={c.id}
                onClick={() => navigate(`/checks/${c.id}`)}
                style={{ display: 'grid', gridTemplateColumns: '1.8fr 1.4fr 1.2fr 0.8fr 0.8fr 0.6fr 60px', gap: 0, padding: '12px 16px', borderBottom: i < checks.length - 1 ? '0.5px solid var(--color-border-light)' : 'none', cursor: 'pointer', alignItems: 'center' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--color-bg-secondary)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 26, height: 26, borderRadius: '50%', background: colours.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: colours.color, flexShrink: 0 }}>
                    {getInitials(c.submittedByName)}
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 400, color: 'var(--color-text-primary)' }}>{c.submittedByName || '—'}</div>
                </div>
                <div>
                  <div style={{ fontSize: 12, color: 'var(--color-text-primary)' }}>{c.assetName || '—'}</div>
                  {c.assetReg && <span style={{ fontFamily: 'monospace', fontSize: 9, background: '#FAC775', color: '#412402', padding: '0 4px', borderRadius: 2 }}>{c.assetReg}</span>}
                </div>
                <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{c.branchName || c.branchId || '—'}</div>
                <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{formatTime(c.submittedAt)}</div>
                <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{c.mileage ? `${c.mileage.toLocaleString()} mi` : '—'}</div>
                <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 20, background: rs.bg, color: rs.color, width: 'fit-content' }}>{rs.label}</span>
                <div style={{ fontSize: 12, color: 'var(--color-brand)', textAlign: 'right' }}>View</div>
              </div>
            );
          })}
        </div>
      </div>
    </Layout>
  );
}